import { useEffect, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { DocumentType, Shipment } from "@api/graphql/generated/generated-types";
import { useCourieStore } from "@src/common/lib/store";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import {
  GET_COMPLIANCE_POLICIES,
  GetCompliancePoliciesData,
  GetCompliancePoliciesVars,
} from "@src/driverCompliancePolicies/graphql/queries";
import { useAutoFillDocumentTypes } from "@src/driverCompliancePolicies";
import { findLocationId } from "@src/shipments/utils/stopFieldFinders";
import { contributingPackageTypeIds } from "@src/common/utils/packageTypeDefaults";

/**
 * Manages driver credential state for the edit order form.
 *
 * - Loads the order's existing policy on mount.
 * - Runs the cascade (courier → end customer → locations) in the background.
 * - Only applies cascade results when the dispatcher actively changes the
 *   end customer, a stop location, or package types after initial load.
 */
export function useEditOrderDriverCredentials({
  courierId,
  orderId,
  selectedCustomerId,
  watchStops,
  shipmentSelectedForAction,
  packageTypeId,
  packageItems,
}: {
  courierId: string | undefined;
  orderId: string | undefined;
  selectedCustomerId: string | undefined;
  watchStops: any[] | undefined;
  shipmentSelectedForAction: Shipment | undefined;
  packageTypeId?: string | null;
  packageItems?: { packageTypeId?: string | null }[] | null;
}) {
  const { showToast } = useCourieStore();
  const [requiredDocTypes, setRequiredDocTypes] = useState<DocumentType[]>([]);
  const [warnedDocTypes, setWarnedDocTypes] = useState<DocumentType[]>([]);

  // --- Initial load: fetch existing policy saved on this order ---
  const [fetchPolicy] = useLazyQuery<
    GetCompliancePoliciesData,
    GetCompliancePoliciesVars
  >(GET_COMPLIANCE_POLICIES, { fetchPolicy: "network-only" });

  const savedPolicySeq = useRef(0);

  useEffect(() => {
    if (!courierId || !orderId) return;
    const seq = ++savedPolicySeq.current;
    fetchPolicy({ variables: { courierId, entityIds: [orderId] } })
      .then((res) => {
        if (seq !== savedPolicySeq.current) return;
        const policy = res.data?.driverCompliancePolicies?.[0];
        if (policy) {
          setRequiredDocTypes(policy.requiredDocumentTypes ?? []);
          setWarnedDocTypes(policy.warnedDocumentTypes ?? []);
        }
      })
      .catch((err) => {
        if (seq !== savedPolicySeq.current) return;
        showErrorToast(err, showToast);
      });
  }, [courierId, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Cascade recompute on customer / location changes ---
  const locationIds: string[] = (() => {
    if (!watchStops || !shipmentSelectedForAction) return [];
    return watchStops
      .map((stop: any) => findLocationId(stop, shipmentSelectedForAction))
      .filter(Boolean) as string[];
  })();

  const packageTypeIds = contributingPackageTypeIds(packageTypeId, packageItems);

  const cascadeResult = useAutoFillDocumentTypes(
    courierId,
    selectedCustomerId,
    locationIds,
    packageTypeIds,
  );

  // Track whether the dispatcher has changed inputs since form initialised.
  // We don't want the initial cascade result to overwrite the saved policy.
  const isInitializedRef = useRef(false);
  const prevCustomerIdRef = useRef<string | undefined>(undefined);
  const prevLocationIdsRef = useRef<string>("");
  const prevPackageTypeIdsRef = useRef<string>("");
  const shouldApplyCascadeRef = useRef(false);

  const locationIdsStr = locationIds.join(",");
  const packageTypeIdsStr = packageTypeIds.join(",");

  useEffect(() => {
    if (!isInitializedRef.current) {
      if (courierId && selectedCustomerId !== undefined) {
        prevCustomerIdRef.current = selectedCustomerId;
        prevLocationIdsRef.current = locationIdsStr;
        prevPackageTypeIdsRef.current = packageTypeIdsStr;
        isInitializedRef.current = true;
      }
      return;
    }

    const customerChanged = selectedCustomerId !== prevCustomerIdRef.current;
    const locationsChanged = locationIdsStr !== prevLocationIdsRef.current;
    const packageTypesChanged =
      packageTypeIdsStr !== prevPackageTypeIdsRef.current;

    if (customerChanged || locationsChanged || packageTypesChanged) {
      shouldApplyCascadeRef.current = true;
      savedPolicySeq.current += 1;
      prevCustomerIdRef.current = selectedCustomerId;
      prevLocationIdsRef.current = locationIdsStr;
      prevPackageTypeIdsRef.current = packageTypeIdsStr;
    }
  }, [courierId, selectedCustomerId, locationIdsStr, packageTypeIdsStr]);

  useEffect(() => {
    if (shouldApplyCascadeRef.current) {
      setRequiredDocTypes(cascadeResult.required);
      setWarnedDocTypes(cascadeResult.warned);
      shouldApplyCascadeRef.current = false;
    }
  }, [cascadeResult]);

  return {
    requiredDocTypes,
    setRequiredDocTypes,
    warnedDocTypes,
    setWarnedDocTypes,
  };
}
